import { Badge, Box, Card, Spinner, Text } from '@chakra-ui/react'
import { convert as htmlToText } from 'html-to-text'
import { useTranslation } from 'react-i18next'
import type { NavigateFunction } from 'react-router'

import type { WebhookConfigListItem } from '@mangovoice/types'

import type { ApiError } from '../api/types'
import type { WebhooksApi } from '../api/webhooksApi'
import { useListWebhooks } from '../hooks/useListWebhooks'
import { formatName } from '../utils'

export interface WebhooksListPageProps {
  api: WebhooksApi
  pbxId: number
  navigate: NavigateFunction
  webhookDetailRoute: (integrationId: number) => string
  imageBaseUrl?: string
}

export const WebhooksListPage = ({
  api,
  pbxId,
  navigate,
  webhookDetailRoute,
  imageBaseUrl = '',
}: WebhooksListPageProps) => {
  const { t } = useTranslation()
  const {
    data: integrations,
    isLoading,
    error,
  } = useListWebhooks({ api, pbxId })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <Spinner />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={6}>
        <Text color="fg.error">
          {(error as ApiError).message ?? t('webhooks.list.failedToLoad')}
        </Text>
      </Box>
    )
  }

  if (!integrations || integrations.length === 0) {
    return (
      <Box p={6}>
        <Text color="fg.muted">
          {t('webhooks.list.noIntegrationsAvailable')}
        </Text>
      </Box>
    )
  }

  return (
    <Box p={6} overflowY="auto" height="full" width="full">
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))"
        gap={4}
      >
        {integrations.map((integration: WebhookConfigListItem) => {
          const activationBadge = integration.activation.is_active
            ? { colorPalette: 'success', label: t('webhooks.list.integrated') }
            : { colorPalette: 'neutral', label: t('webhooks.list.learnMore') }
          return (
            <Card.Root
              key={integration.id}
              cursor="pointer"
              onClick={() => navigate(webhookDetailRoute(integration.id))}
              _hover={{ shadow: 'md' }}
            >
              <Card.Body>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={3}
                  height="full"
                >
                  {integration.img_url && (
                    <Box>
                      <img
                        src={`${imageBaseUrl}${integration.img_url}`}
                        alt={integration.name}
                        loading="lazy"
                        style={{
                          maxWidth: 160,
                          maxHeight: 80,
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  )}
                  {/* Stop propagation so that any links rendered inside offer/description text don't also
                    trigger card navigation. offer/description may contain HTML markup from the DB. */}
                  <Box onClick={(e) => e.stopPropagation()} cursor="default">
                    {!integration.img_url && (
                      <Text fontWeight="semibold">
                        {formatName(integration.name)}
                      </Text>
                    )}
                    {(integration.offer || integration.description) && (
                      <Text fontSize="sm" color="fg.muted" mt={1}>
                        {htmlToText(
                          integration.offer || integration.description || '',
                          { wordwrap: false },
                        )}
                      </Text>
                    )}
                  </Box>
                  <Box
                    marginTop="auto"
                    display="flex"
                    gap={2}
                    alignItems="center"
                  >
                    {integration.status === 'pending' && (
                      <Badge colorPalette="warning" variant="subtle">
                        {t('webhooks.common.beta')}
                      </Badge>
                    )}
                    <Badge
                      colorPalette={activationBadge.colorPalette}
                      variant="subtle"
                    >
                      {activationBadge.label}
                    </Badge>
                  </Box>
                </Box>
              </Card.Body>
            </Card.Root>
          )
        })}
      </Box>
    </Box>
  )
}
